import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { ArrowRight, Sparkles, Users, BookOpen, Globe } from "lucide-react";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import axios from "axios";
import { API } from "../App";

const Login = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate("/permisos", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = async () => {
    if (!auth || !googleProvider) {
      alert("Error: Firebase no está configurado. Por favor, contacta al administrador.");
      return;
    }

    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      
      // Send token to backend
      const response = await axios.post(
        `${API}/auth/firebase`,
        { idToken },
        { withCredentials: true }
      );
      
      const { session_token, user: userData } = response.data;
      await login(session_token, userData);
      navigate("/permisos", { replace: true });
    } catch (error) {
      console.error("Firebase login error:", error);
      let errorMessage = "Error al iniciar sesión. Por favor, intenta de nuevo.";
      
      if (error.response?.status === 503) {
        errorMessage = "El servicio de autenticación no está disponible. Por favor, verifica la configuración del servidor.";
      } else if (error.response?.status === 401) {
        errorMessage = "Credenciales inválidas. Por favor, intenta de nuevo.";
      } else if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "La ventana de inicio de sesión fue cerrada. Por favor, intenta de nuevo.";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage = "La ventana emergente fue bloqueada. Por favor, permite ventanas emergentes e intenta de nuevo.";
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-primary-50 to-secondary-50">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-primary-50/30 to-secondary-50/30">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1565557531267-1d5bedaae2bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGxpcXVpZCUyMGdsYXNzJTIwYmx1ZSUyMGN5YW4lMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2OTMxNzUwMnww&ixlib=rb-4.1.0&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-secondary/60 backdrop-blur-sm"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center p-8 lg:p-12 text-white">
          <div className="max-w-lg">
            <div className="flex items-center gap-3 mb-8">
              <img
                src="/logo-wca.jpeg"
                alt="WCA Logo"
                className="w-14 h-14 rounded-2xl object-cover shadow-lg"
              />
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold">Ecosistema WCA</h1>
                <p className="text-white/80 text-sm">World Community Academy</p>
              </div>
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-6">
              Ecosistema de Gestión
              <span className="block text-secondary-200">TechHuman</span>
            </h2>
            
            <p className="text-base lg:text-lg text-white/90 mb-10 leading-relaxed">
              Una plataforma integral que combina tecnología y humanidad para transformar 
              la educación y el impacto social.
            </p>

            {/* Feature cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-md rounded-2xl p-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Users size={24} />
                </div>
                <div>
                  <h3 className="font-semibold">Gestión de Talento</h3>
                  <p className="text-sm text-white/70">Cuida a tu equipo con herramientas de bienestar</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-md rounded-2xl p-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <BookOpen size={24} />
                </div>
                <div>
                  <h3 className="font-semibold">Academia Innovadora</h3>
                  <p className="text-sm text-white/70">Diseña rutas de aprendizaje transformadoras</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-md rounded-2xl p-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Globe size={24} />
                </div>
                <div>
                  <h3 className="font-semibold">Impacto Global</h3>
                  <p className="text-sm text-white/70">Mide y amplifica tu impacto en comunidades</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-5 justify-center">
            <img
              src="/banner-wca.png"
              alt="WCA Logo"
              className="h-20 sm:h-24"
            />
          </div>

          <div className="glass-panel p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                <Sparkles size={32} className="text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Bienvenido
              </h2>
              <p className="text-slate-500">
                Inicia sesión para acceder al Ecosistema WCA
              </p>
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full btn-primary flex items-center justify-center gap-3 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="google-login-btn"
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>{isLoading ? "Iniciando sesión..." : "Continuar con Google"}</span>
              {!isLoading && <ArrowRight size={18} />}
            </button>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-center text-xs text-slate-400 mb-2">
                <Link to="/politica-privacidad" className="hover:text-primary hover:underline">Política de privacidad</Link>
                <span className="mx-1.5">·</span>
                <Link to="/terminos-servicio" className="hover:text-primary hover:underline">Condiciones del servicio</Link>
              </p>
              <p className="text-center text-sm text-slate-500">
                <span className="text-primary">© 2026 World Community Academy.</span>
                <br/>
                Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
